import * as React from "react";
import { useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";

const GC_STATUSES = ["new", "authorizing", "authorized", "executed", "rejected"];
const MAC_STATUSES = [
  "pending_payment", "payment_failed", "ordering", "ordered",
  "activated", "expired", "failed", "swept", "cancelled", "archived",
];

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(4),
    width: "100%",
    maxWidth: 480,
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
}));

const UpdateGiftCardTransaction: React.FC = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [reference, setReference] = useState("");
  const [transactionType, setTransactionType] = useState("giftcard");
  const [status, setStatus] = useState("");
  const [isRefunded, setIsRefunded] = useState("");
  const [spriveCancelled, setSpriveCancelled] = useState("");
  const [saving, setSaving] = useState(false);

  const isAnywhere = transactionType === "anywhere_card";

  const handleSave = async () => {
    if (!reference.trim()) {
      notify(isAnywhere ? "Enter a Payment ID" : "Enter a Simp ID", "error");
      return;
    }
    setSaving(true);
    try {
      const accessToken = localStorage.getItem("accessToken") || "";
      const body: any = {
        type: transactionType,
        reference: reference.trim(),
      };
      if (status) body.status = status;
      if (isRefunded !== "") body.is_refunded = Number(isRefunded);
      if (!isAnywhere && spriveCancelled !== "") body.sprive_cancelled = Number(spriveCancelled);

      await axios.patch(ENDPOINTS.UPDATE_GIFT_CARD_PAYMENT, body, {
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      });

      notify("Updated successfully", "info");
      setStatus("");
      setIsRefunded("");
      setSpriveCancelled("");
    } catch (err: any) {
      notify(err?.response?.data?.message || "Update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classes.main}>
      <Paper className={classes.paper} elevation={3}>
        <Typography variant="h6" gutterBottom>
          Update GC Payment
        </Typography>

        <FormControl variant="outlined" size="small" fullWidth className={classes.field}>
          <InputLabel>Type</InputLabel>
          <Select
            value={transactionType}
            onChange={(e) => {
              setTransactionType(e.target.value as string);
              setStatus("");
            }}
            label="Type"
          >
            <MenuItem value="giftcard">Gift Card</MenuItem>
            <MenuItem value="anywhere_card">Anywhere Card</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label={isAnywhere ? "Payment ID" : "Simp ID"}
          variant="outlined"
          size="small"
          fullWidth
          className={classes.field}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />

        <FormControl variant="outlined" size="small" fullWidth className={classes.field}>
          <InputLabel>Status</InputLabel>
          <Select value={status} onChange={(e) => setStatus(e.target.value as string)} label="Status">
            <MenuItem value="">— unchanged —</MenuItem>
            {(isAnywhere ? MAC_STATUSES : GC_STATUSES).map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl variant="outlined" size="small" fullWidth className={classes.field}>
          <InputLabel>Is Refunded</InputLabel>
          <Select value={isRefunded} onChange={(e) => setIsRefunded(e.target.value as string)} label="Is Refunded">
            <MenuItem value="">— unchanged —</MenuItem>
            <MenuItem value="1">Yes</MenuItem>
            <MenuItem value="0">No</MenuItem>
          </Select>
        </FormControl>

        {!isAnywhere && (
          <FormControl variant="outlined" size="small" fullWidth className={classes.field}>
            <InputLabel>Sprive Cancelled</InputLabel>
            <Select value={spriveCancelled} onChange={(e) => setSpriveCancelled(e.target.value as string)} label="Sprive Cancelled">
              <MenuItem value="">— unchanged —</MenuItem>
              <MenuItem value="1">Yes</MenuItem>
              <MenuItem value="0">No</MenuItem>
            </Select>
          </FormControl>
        )}

        <Box className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Paper>
    </div>
  );
};

export default UpdateGiftCardTransaction;
